var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { selectAppContent, selectCurrentAgendaSlide, selectCurrentDrawer, selectCurrentEpisode, setCurrentAgendaSelected, setDrawerType, setMobileDrawerOpen } from "@adm-media/adam-store-plugin";
import { LeftOutlinedIcon, RightOutlined } from "../Icons/Icons";
import { useSelector } from "react-redux";
import Button from "../Buttons/Button";
import classNames from "classnames";
import useBreakpoint from "../../../hooks/useBreakpoint";
import { useAppDispatch } from "../../../store";
import SlideMediaItem from "../SlideMediaItem/SlideMediaItem";
import { isEmpty } from "../../../utils/obj-check";
import "./SlidesPlayer.scss";
var SlidesPlayer = function (_a) {
    var playerRef = _a.playerRef;
    var _b = useState([]), slides = _b[0], setSlides = _b[1];
    var _c = useState(), currentSlide = _c[0], setCurrentSlide = _c[1];
    var dispatch = useAppDispatch();
    var bottomRef = useRef(null);
    var slideRefs = useRef([]);
    var isDragging = useRef(false);
    var startX = useRef(0);
    var scrollLeft = useRef(0);
    var contents = useSelector(selectAppContent);
    var _d = useBreakpoint(), isMobile = _d.isMobile, isMiniTablet = _d.isMiniTablet, isTablet = _d.isTablet;
    var isSmallScreen = isMobile || isMiniTablet || isTablet;
    var isBigScreen = !isSmallScreen;
    var currentEpisode = useSelector(selectCurrentEpisode);
    var currentAgendaSelected = useSelector(selectCurrentAgendaSlide);
    var currentDrawerType = useSelector(selectCurrentDrawer);
    // ========= DRAG & SCROLL =========//
    var onMouseDown = function (e) {
        if (!bottomRef.current)
            return;
        e.preventDefault();
        isDragging.current = true;
        bottomRef.current.classList.add("dragging");
        startX.current = e.pageX - bottomRef.current.offsetLeft;
        scrollLeft.current = bottomRef.current.scrollLeft;
    };
    var onMouseLeave = function () {
        var _a;
        isDragging.current = false;
        (_a = bottomRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove("dragging");
    };
    var onMouseUp = function () {
        var _a;
        isDragging.current = false;
        (_a = bottomRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove("dragging");
    };
    var onMouseMove = function (e) {
        if (!isDragging.current || !bottomRef.current)
            return;
        e.preventDefault();
        var x = e.pageX - bottomRef.current.offsetLeft;
        var walk = (x - startX.current) * 1.5; // scroll speed
        bottomRef.current.scrollLeft = scrollLeft.current - walk;
    };
    var onSingleClickMedia = function (id) {
        // Retrieve the slide and update state on correct slide
        setCurrentSlide(slides.find(function (singleSlide) { return singleSlide.id === id; }));
        // SCROLL TO ELEMENT
        var slideElement = slideRefs.current[id - 1]; // slides start at id 1
        if (slideElement && bottomRef.current) {
            var container = bottomRef.current;
            // Calculate the horizontal center of the slide relative to the container
            var slideLeft = slideElement.offsetLeft;
            var slideWidth = slideElement.offsetWidth;
            var containerWidth = container.offsetWidth;
            // Scroll so the slide is centered
            var targetScrollLeft = slideLeft - containerWidth / 2 + slideWidth / 2;
            container.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
        }
    };
    var goToTimeStamp = function (time) {
        if (!playerRef)
            return;
        playerRef.currentTime = time;
        dispatch(setDrawerType(undefined));
        dispatch(setMobileDrawerOpen(false));
    };
    var goNext = function () {
        // If is last item of array, go the first one
        if (currentSlide.id === (slides === null || slides === void 0 ? void 0 : slides.length)) {
            onSingleClickMedia(1);
        }
        else {
            onSingleClickMedia(Math.min(currentSlide.id + 1, (slides === null || slides === void 0 ? void 0 : slides.length) || 0));
        }
    };
    var goBack = function () {
        // If is first item of array, go the last one
        if (currentSlide.id === 1) {
            onSingleClickMedia(slides.length);
        }
        else {
            onSingleClickMedia(Math.max(currentSlide.id - 1, 1));
        }
    };
    // This is the entry point to set data to load
    useEffect(function () {
        var _a;
        if (!currentEpisode || !currentAgendaSelected)
            return;
        // Get only slides with slides_config field
        var onlyAgendaWithSlides = currentEpisode.agenda.filter(function (element) { return element.slides_config !== null; });
        if (onlyAgendaWithSlides.length === 0 || !onlyAgendaWithSlides)
            return;
        var currentAgendaSlides = (_a = onlyAgendaWithSlides.find(function (agenda) { return agenda.id === (currentAgendaSelected === null || currentAgendaSelected === void 0 ? void 0 : currentAgendaSelected.id); })) === null || _a === void 0 ? void 0 : _a.slides_config;
        if (!currentAgendaSlides)
            return;
        // Provide a FE ONLY "id" field to perform next,prev operation
        var slidesWithId = currentAgendaSlides.map(function (item, i) { return (__assign(__assign({}, item), { id: i + 1 })); });
        setSlides(slidesWithId || []);
        setCurrentSlide(slidesWithId[0]);
    }, [currentEpisode, currentAgendaSelected]);
    useEffect(function () {
        return function () {
            dispatch(setCurrentAgendaSelected(null));
        };
    }, []);
    return (_jsx("div", __assign({ className: classNames("slides-player", { isSmallScreen: isSmallScreen, isBigScreen: isBigScreen, isSlideSelected: currentSlide }) }, { children: isBigScreen && !isEmpty(slides) && (_jsxs(_Fragment, { children: [_jsxs("div", __assign({ className: "top" }, { children: [_jsx("div", __assign({ className: "arrow arrow-left", onClick: goBack }, { children: _jsx(LeftOutlinedIcon, {}) })), _jsx("div", __assign({ className: "preview-box" }, { children: _jsx("img", { src: currentSlide === null || currentSlide === void 0 ? void 0 : currentSlide.url, alt: "" }) })), _jsx("div", __assign({ className: "arrow arrow-right", onClick: goNext }, { children: _jsx(RightOutlined, {}) }))] })), _jsx("div", __assign({ className: "center" }, { children: _jsx(Button, __assign({ className: "btn-go-to", onClick: function () { return goToTimeStamp(currentSlide === null || currentSlide === void 0 ? void 0 : currentSlide.time); } }, { children: contents.slide_player_go_to || "Go to section" })) })), _jsx("div", __assign({ ref: bottomRef, className: "bottom", onMouseDown: onMouseDown, onMouseLeave: onMouseLeave, onMouseUp: onMouseUp, onMouseMove: onMouseMove }, { children: slides === null || slides === void 0 ? void 0 : slides.map(function (slide, index) { return (_jsx("div", __assign({ className: "draggable-slide", ref: function (el) { return (slideRefs.current[index] = el); } }, { children: _jsx(SlideMediaItem, { slide: slide, onSingleClickMedia: function () { return onSingleClickMedia(slide.id); }, goToTimeStamp: function (id) { return goToTimeStamp(id); }, isSmallScreen: isSmallScreen, isActive: slide.id === (currentSlide === null || currentSlide === void 0 ? void 0 : currentSlide.id) }) }), slide.time)); }) }))] })) })));
};
export default SlidesPlayer;
